import React, { useState, useEffect, useRef } from 'react';

import NavLink from 'components/NavLink';
import Tabbar from 'components/Tabbar/Tabbar';
import Tab from 'components/Tabbar/Tab';
import Backdrop from '@mui/material/Backdrop';
import styled from '@emotion/styled/macro';
import history from 'providers/history';
import Search from 'containers/Search/Search';
import Grid from 'components/Grid';
import AppsRoundedIcon from '@mui/icons-material/AppsRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import useNavigationItems from 'containers/Sidebar/useNavigationItems';
import useFeature from 'hooks/useFeature';
import { useSelector } from 'react-redux';
import { authSelectors } from 'redux/auth';
import Hub from './Hub';
import HubButton from './HubButton';
import AddButton from './AddButton';

const NavbarBackdrop = styled(Backdrop)`
  z-index: ${({ theme }) => theme.zIndex.navbar};
`;

const GridWithActualGrid = styled(Grid)`
  display: grid;

  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));

  @media(min-width: 321px) {
    grid-template-columns: repeat(auto-fit, minmax(120px, 0.5fr));
  }
`;

const TabbarContainer = () => {

  const [hubMenuOpen, setHubMenuOpen] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [hidden, setHidden] = useState(false);

  const postFeature = useFeature('post');

  // Search reaches nothing a member waiting on approval is allowed to see
  const pending = useSelector(authSelectors.isPendingMember);

  const { hubItems } = useNavigationItems();

  // Five icons at most: the tabs, the hub (or search) button and the post button
  const slotCount = postFeature.enabled ? 4 : 5;
  const allItemsFit = hubItems.length + (pending ? 0 : 1) <= slotCount;
  const tabCount = allItemsFit ? hubItems.length : slotCount - 1;

  const tabButtons = hubItems.slice(0, tabCount);
  const hubButtons = hubItems.slice(tabCount);

  const hasHubButtons = hubButtons.length > 0;

  const searchInputRef = useRef(null);

  // The drawer keeps its content mounted while closed, so autoFocus would only fire on page load
  useEffect(() => {
    if (hubMenuOpen && !hasHubButtons) {
      searchInputRef.current?.focus();
    }
  }, [hubMenuOpen, hasHubButtons]);

  useEffect(() => {
    return history.listen(() => {
      setAddMenuOpen(false);
      setHubMenuOpen(false);
    });
  }, []);

  useEffect(() => {
    const fnSet = () => setHidden(true);
    const fnRemove = () => setHidden(false);

    window.addEventListener('keyboardWillShow', fnSet, { passive: true });
    window.addEventListener('keyboardWillHide', fnRemove, { passive: true });
    return () => {
      window.removeEventListener('keyboardWillShow', fnSet, { passive: true });
      window.removeEventListener('keyboardWillHide', fnRemove, { passive: true });
    };
  }, []);

  const onHubButtonClick = () => {
    setHubMenuOpen(c => !c);
    setAddMenuOpen(false);
  };

  const onAddButtonClick = () => {
    setAddMenuOpen(c => !c);
    setHubMenuOpen(false);
  };


  if (hidden && !hubMenuOpen) {
    return null;
  }

  // Without any hub icons, there will be just search functionality
  const HubIcon = hasHubButtons ? AppsRoundedIcon : SearchRoundedIcon;

  // Without any hub icons, there's no room below the search bar
  const searchProps = hasHubButtons ? {} : {
    popperPlacement: 'top',
    reverseOptions: true,
  };

  return (
    <>

      {addMenuOpen && <NavbarBackdrop open={addMenuOpen} />}

      <Hub
        open={hubMenuOpen}
        onClose={() => setHubMenuOpen(false)}
      >
        {/* Disable autocomplete for search here? */}
        <Grid container spacing={2} direction="column" wrap="nowrap">
          {!pending && (
            <Grid item>
              <Search {...searchProps} inputRef={searchInputRef} />
            </Grid>
          )}
          <Grid item>
            <GridWithActualGrid container spacing={2}>

              {hubButtons.map(({ icon: Icon, children, ...args }) => (
                <Grid item key={args.to}>
                  <HubButton as={NavLink} {...args} icon={<Icon />}>{children}</HubButton>
                </Grid>
              ))}

            </GridWithActualGrid>
          </Grid>
        </Grid>
      </Hub>

      <Tabbar>

        {tabButtons.map(({ icon: Icon, children, ...args }) => (
          <Tab key={args.to} isActive={match => match && !hubMenuOpen} component={NavLink} {...args}><Icon fontSize="inherit" /></Tab>
        ))}

        {(hasHubButtons || !pending) && <Tab onClick={onHubButtonClick}><HubIcon fontSize="inherit" /></Tab>}

        {postFeature.enabled && (
          <AddButton
            open={addMenuOpen}
            onToggle={onAddButtonClick}
            onClose={() => setAddMenuOpen(false)}
          />
        )}
      </Tabbar>
    </>
  );
};

export default TabbarContainer;
